import { useClientStore } from '@/stores/clientStore';
import ApiClient from '@/services/apiClient';
import WeekDay from '@/models/weekDay';
import moment from 'moment';
import i18n from '@/i18n';

const t = (key) => i18n.global.t(key);

export default class Condition {
    id;
    condition_type;
    is_active;
    events;
    input;
    external_input;
    date;
    time;
    days_of_week;
    external_intrusion_state;

    constructor(id = 0) {
        this.id = id;
        this.condition_type = "";
        this.is_active = true;
        this.events = new EventCondition();
        this.input = new InputCondition();
        this.date = new DateCondition();
        this.time = new TimeCondition();
        this.days_of_week = [];
        this.external_intrusion_state = new ExternalIntrusionCondition();
    }

    static constructFromObject(data, obj) {
        if (data) {
            obj = obj || new Condition();
            obj.id = ApiClient.convertToType(data['id'], 'Integer');
            obj.is_active = ApiClient.convertToType(data['is_active'], 'Boolean');
            obj.condition_type = ApiClient.convertToType(data['condition_type'], 'String');
            switch(obj.condition_type) {
                case "event":
                    obj.events = EventCondition.constructFromObject(data['events']);
                    break;
                case "input":
                    obj.input = ApiClient.convertToType(data['input'], InputCondition);
                    break;
                case "date":
                    obj.date = ApiClient.convertToType(data['date'], DateCondition);
                    break;
                case "time":
                    obj.time = ApiClient.convertToType(data['time'], TimeCondition);
                    break;
                case "day_of_week":
                    obj.days_of_week = ApiClient.convertToType(data['days_of_week'], [WeekDay]);
                    break;
                case "external_intrusion_state":
                    obj.external_intrusion_state = ExternalIntrusionCondition.constructFromObject(data['external_intrusion_state']);
                    break;
            }
        }
        return obj;
    }

    toJSON() {
        const json = {
            id: this.id,
            is_active: this.is_active,
            condition_type: this.condition_type,
        };

        switch(this.condition_type) {
            case "event":
                json.events = this.events;
                break;
            case "input":
                json.input = this.input;
                break;
            case "external_input":
                json.external_input = this.external_input;
                break;
            case "date":
                json.date = this.date;
                break;
            case "time":
                json.time = this.time;
                break;
            case "day_of_week":
                json.days_of_week = this.days_of_week.map(item => item.id);
                break;
            case "external_intrusion_state":
                json.external_intrusion_state = this.external_intrusion_state;
                break;
        }
        return json;
    }

    toString() {
        switch(this.condition_type) {
            case "event":
                return this.events.toString();
            case "input":
                return this.input.toString();
            case "external_input":
                return this.external_input.toString();
            case "date":
                return this.date.toString();
            case "time":
                return this.time.toString();
            case "day_of_week":
                return this.getDaysOfWeek();
            case "external_intrusion_state":
                return this.external_intrusion_state.toString();
            default:
                return t('condition.unknown');
        }
    }

    getDaysOfWeek() {
        return `${t('condition.daysOfWeek')} <b>${this.days_of_week.join(", ")}</b>`;
    }
}

export class EventCondition {
    event_type_ids;
    readers;
    has_identifiers;
    identifiers;
    has_readers;
    reader_positions;
    has_groups;
    groups;

    constructor() {
        this.event_type_ids = [];
        this.identifiers = [];
        this.reader_positions = [];
        this.groups = [];
    }

    static constructFromObject(data, obj) {
        obj = obj || new EventCondition();
        if (data) {
            obj.event_type_ids = ApiClient.convertToType(data['event_type_ids'], ['Integer']);
            obj.identifiers = ApiClient.convertToType(data['identifiers'], ['String']) || [];
            obj.has_identifiers = obj.identifiers.length > 0;
            obj.reader_positions = ApiClient.convertToType(data['reader_positions'], ['Integer']) || [];
            obj.has_readers = obj.reader_positions.length > 0;
            obj.groups = ApiClient.convertToType(data['groups'], ['Integer']) || [];
            obj.has_groups = obj.groups.length > 0;
        }
        return obj;
    }

    toJSON() {
        return {
            event_type_ids: this.event_type_ids,
            identifiers: this.has_identifiers? this.identifiers : [],
            reader_positions: this.has_readers? this.reader_positions : [],
            groups: this.has_groups? this.groups : [],
        }
    }

    toString() {
        const clientStore = useClientStore();
        let text = `${t('condition.event')} <b>${clientStore.getEventTypes(this.event_type_ids).join(" ou ")}</b>`;
        if(this.has_readers) {
            switch(this.reader_positions.length) {
                case 0:
                    break;
                case 1:
                    text += ` ${t('condition.reader')} <b>${clientStore.getReaders(this.reader_positions)}</b>`;
                    break;
                default:
                    text += ` ${t('condition.readers')} <b>${clientStore.getReaders(this.reader_positions).join(", ")}</b>`;
                    break;
            }
        }
        if(this.has_groups) {
            switch(this.groups.length) {
                case 0:
                    break;
                case 1:
                    text += ` ${t('condition.group')} <b>${clientStore.getAccessGroups(this.groups)}</b>`;
                    break;
                default:
                    text += ` ${t('condition.groups')} <b>${clientStore.getAccessGroups(this.groups).join(", ")}</b>`;
                    break;
            }
        }
        if(this.has_identifiers) {
            switch(this.identifiers.length) {
                case 0:
                    break;
                case 1:
                    text += ` ${t('condition.identifier')} <b>${this.identifiers[0]}</b>`;
                    break;
                default:
                    text += ` ${t('condition.identifiers')} <b>${this.identifiers.join(", ")}</b>`;
                    break;
            }
        }
        return text;
    }
}

export class InputCondition {
    input_number;
    state;

    constructor() {
        this.input_number = "";
        this.state = "";
    }

    static constructFromObject(data, obj) {
        obj = obj || new InputCondition();
        if (data) {
            obj.input_number = ApiClient.convertToType(data['input_number'], 'Integer');
            obj.state = ApiClient.convertToType(data['state'], 'Integer');
        }
        return obj;
    }

    toString() {
        const clientStore = useClientStore();
        const stateLabel = this.state === 0 ? t('condition.inputInactive') : t('condition.inputActive');
        return `${t('condition.inputCondition')} <b>${clientStore.getInputs(this.input_number)} ${stateLabel}</b>`;
    }
}

export class ExternalIntrusionCondition {
    id;
    state;

    constructor() {
        this.id = "";
        this.state = "";
    }

    static constructFromObject(data, obj) {
        obj = obj || new ExternalIntrusionCondition();
        if (data) {
            obj.id = ApiClient.convertToType(data['id'], 'Integer');
            obj.state = ApiClient.convertToType(data['state'], 'String');
        }
        return obj;
    }

    toString() {
        const clientStore = useClientStore();
        const alarm = clientStore.getExternalAlarms(this.id)[0];
        const stateLabel = this.state === 'Armed' ? t('condition.intrusionArmed') : t('condition.intrusionDisarmed');
        return `${t('condition.externalIntrusion')} <b>${alarm ? alarm.name : this.id} ${stateLabel}</b>`;
    }
}

export class DateCondition {
    start;
    end;

    constructor() {
        this.start = "";
        this.end = "";
    }

    static constructFromObject(data, obj) {
        obj = obj || new DateCondition();
        if (data) {
            obj.start = ApiClient.convertToType(data['start'], 'String');
            obj.end = ApiClient.convertToType(data['end'], 'String');
        }
        return obj;
    }

    toString() {
        if(this.end) {
            return `${t('condition.dateBetween')} <b>${moment(this.start).format("DD/MM/YYYY")}</b> ${t('condition.dateAnd')} <b>${moment(this.end).format("DD/MM/YYYY")}</b>`;
        } else {
            return `${t('condition.dateAfter')} <b>${moment(this.start).format("DD/MM/YYYY")}</b>`;
        }
    }

    check() {
        if(this.end.toString().length) {
            return Date.parse(this.end) > Date.parse(this.start);
        }

        return true;
    }
}

export class TimeCondition {
    start;
    end;

    constructor() {
        this.start = null;
        this.end = null;
    }

    static constructFromObject(data, obj) {
        obj = obj || new TimeCondition();
        if (data) {
            obj.start = ApiClient.convertToType(data['start'], 'Integer');
            obj.end = ApiClient.convertToType(data['end'], 'Integer');
        }
        return obj;
    }

    toString() {
        if(this.end) {
            return `${t('condition.timeBetween')} <b>${this.startAsTime}</b> ${t('condition.timeAnd')} <b>${this.endAsTime}</b>`;
        } else {
            return `${t('condition.timeAfter')} <b>${this.startAsTime}</b>`;
        }
    }

    check() {
        return this.end == null? true : this.end >= this.start;
    }

    secondsToTime(secondsSinceMidnight) {
        return secondsSinceMidnight == null? null : moment().startOf('day').add(secondsSinceMidnight, 'seconds').format('HH:mm');
    }

    get startAsTime() {
        return this.secondsToTime(this.start);
    }

    set startAsTime(time) {
        this.start = time == null? null : moment.duration(time).asSeconds();
    }

    get endAsTime() {
        return this.secondsToTime(this.end);
    }

    set endAsTime(time) {
        this.end = time == null? null : moment.duration(time).asSeconds();
    }
}
